import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

interface DashboardStats {
  users: {
    total: number;
    activeToday: number;
    activeThisMonth: number;
  };
  companions: {
    total: number;
  };
  conversations: {
    total: number;
    messagesTotal: number;
    messagesToday: number;
  };
  revenue: {
    total: number;
    thisMonth: number;
  };
  tokens: {
    usedToday: number;
  };
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchDashboardStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/admin/stats', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Admin access required');
        navigate('/login');
      } else {
        setError('Failed to load dashboard stats');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-lg">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <StatCard
            title="Total Users"
            value={stats?.users.total || 0}
            subtitle={`${stats?.users.activeToday || 0} active today`}
            bgColor="bg-blue-500"
          />

          <StatCard
            title="Active This Month"
            value={stats?.users.activeThisMonth || 0}
            subtitle="Monthly active users"
            bgColor="bg-green-500"
          />

          <StatCard
            title="Total Companions"
            value={stats?.companions.total || 0}
            subtitle="AI companions created"
            bgColor="bg-purple-500"
          />

          <StatCard
            title="Total Conversations"
            value={stats?.conversations.total || 0}
            subtitle={`${stats?.conversations.messagesToday || 0} messages today`}
            bgColor="bg-indigo-500"
          />

          <StatCard
            title="Total Messages"
            value={stats?.conversations.messagesTotal || 0}
            subtitle="All-time messages"
            bgColor="bg-pink-500"
          />

          <StatCard
            title="Revenue (Total)"
            value={`$${(stats?.revenue.total || 0).toFixed(2)}`}
            subtitle={`$${(stats?.revenue.thisMonth || 0).toFixed(2)} this month`}
            bgColor="bg-yellow-500"
          />

          <StatCard
            title="Tokens Used Today"
            value={stats?.tokens.usedToday || 0}
            subtitle="API token consumption"
            bgColor="bg-red-500"
          />
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <QuickActions />
          <RecentActivity />
        </div>
      </main>
    </div>
  );
};

interface StatCardProps {
  title: string;
  value: number | string;
  subtitle: string;
  bgColor: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, bgColor }) => {
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`${bgColor} rounded-md p-3`}>
              <div className="text-white text-2xl font-bold">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </div>
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-xs text-gray-400 mt-1">{subtitle}</dd>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuickActions: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
      <div className="space-y-3">
        <button
          onClick={() => navigate('/admin/users')}
          className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md transition-colors"
        >
          Manage Users
        </button>
        <button
          onClick={() => navigate('/admin/conversations')}
          className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md transition-colors"
        >
          View Conversations
        </button>
        <button
          onClick={() => navigate('/admin/metrics')}
          className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md transition-colors"
        >
          System Metrics
        </button>
        <button
          onClick={() => navigate('/admin/activity')}
          className="w-full text-left px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 rounded-md transition-colors"
        >
          Activity Logs
        </button>
      </div>
    </div>
  );
};

const RecentActivity: React.FC = () => {
  const [activities, setActivities] = useState<any[]>([]);

  useEffect(() => {
    fetchRecentActivity();
  }, []);

  const fetchRecentActivity = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/admin/activity?limit=5', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setActivities(response.data);
    } catch (err) {
      console.error('Failed to fetch activity:', err);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
      <div className="space-y-3">
        {activities.length === 0 ? (
          <p className="text-gray-500 text-sm">No recent activity</p>
        ) : (
          activities.map((activity, index) => (
            <div key={index} className="border-b border-gray-200 pb-2">
              <div className="text-sm">
                <span className="font-medium">{activity.user?.email || 'System'}</span>
                <span className="text-gray-500"> - {activity.action}</span>
              </div>
              <div className="text-xs text-gray-400">
                {new Date(activity.createdAt).toLocaleString()}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
